import React from 'react';
import { useTranslation } from 'react-i18next';
import useScrollReveal from '../hooks/useScrollReveal';

const AboutPage = () => {
  const [heroRef, heroVisible] = useScrollReveal();
  const [bioRef, bioVisible] = useScrollReveal();
  const [processRef, processVisible] = useScrollReveal();
  const [quotesRef, quotesVisible] = useScrollReveal();
  const { t, i18n } = useTranslation();

  const isEnglish = i18n.language?.startsWith('en');

  const milestones = [
    { year: '1962', event: t('about.milestones.birth') },
    { year: '1985', event: t('about.milestones.graduation') },
    { year: '1990', event: t('about.milestones.firstExhibition') },
    { year: '1995-2005', event: t('about.milestones.landscapes') },
    { year: '2006-2015', event: t('about.milestones.portraits') },
    { year: isEnglish ? '2016-present' : '2016-н.в.', event: t('about.milestones.experiments') },
  ];

  const processSteps = [
    {
      step: '01',
      title: t('about.process.step1.title'),
      desc: t('about.process.step1.description'),
    },
    {
      step: '02',
      title: t('about.process.step2.title'),
      desc: t('about.process.step2.description'),
    },
    {
      step: '03',
      title: t('about.process.step3.title'),
      desc: t('about.process.step3.description'),
    },
  ];

  const reviews = [
    {
      quote: t('about.reviews.review1.text'),
      author: t('about.reviews.review1.author'),
      role: t('about.reviews.review1.role'),
    },
    {
      quote: t('about.reviews.review2.text'),
      author: t('about.reviews.review2.author'),
      role: t('about.reviews.review2.role'),
    },
  ];

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      {/* Hero Section */}
      <section
        ref={heroRef}
        className="pt-32 pb-20 px-6 lg:px-8"
      >
        <div className="max-w-7xl mx-auto">
          <div className={`text-center reveal ${heroVisible ? 'visible' : ''}`}>
            <span className="text-sm text-[#B8963E] tracking-widest uppercase mb-4 block">
              {t('about.label')}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl mb-6 text-[#3D3831]">
              {t('about.title')}
            </h1>
            <div className="divider-elegant mb-6" />
            <p className="text-[#8B7E6A] max-w-2xl mx-auto text-lg">
              {t('about.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Main Bio Section */}
      <section ref={bioRef} className="py-16 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-16">
            {/* Portrait */}
            <div className={`lg:w-2/5 reveal ${bioVisible ? 'visible' : ''}`}>
              <div className="sticky top-32">
                <div className="relative">
                  <img
                    src="/pics/IVP.png"
                    alt={t('about.name')}
                    className="w-full shadow-2xl"
                  />
                  <div className="absolute -bottom-4 -right-4 w-full h-full border-2 border-[#B8963E]/30 -z-10" />
                </div>

                {/* Contact Card */}
                <div className="mt-12 p-6 bg-white shadow-lg">
                  <h3 className="text-lg mb-4 text-[#3D3831]">{t('about.contact')}</h3>
                  <div className="space-y-3 text-sm">
                    <p className="text-[#5A5249]">
                      <span className="text-[#8B7E6A]">Email:</span>{' '}
                      info@irina-podlevskikh.ru
                    </p>
                    <p className="text-[#5A5249]">
                      <span className="text-[#8B7E6A]">{t('about.studio')}:</span>{' '}
                      {t('about.studioLocation')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Bio Content */}
            <div className={`lg:w-3/5 reveal reveal-delay-2 ${bioVisible ? 'visible' : ''}`}>
              <h2 className="text-3xl mb-6 text-[#3D3831]">{t('about.name')}</h2>

              <div className="prose prose-lg max-w-none">
                <p className="text-[#5A5249] leading-relaxed mb-6">
                  {t('about.bio1')}
                </p>

                <p className="text-[#5A5249] leading-relaxed mb-6">
                  {t('about.bio2')}
                </p>

                <p className="text-[#5A5249] leading-relaxed mb-6">
                  {t('about.bio3')}
                </p>

                {/* Quote */}
                <blockquote className="my-10 pl-6 border-l-2 border-[#B8963E]">
                  <p className="text-xl italic text-[#3D3831] mb-4">
                    "{t('about.quote')}"
                  </p>
                  <cite className="text-sm text-[#8B7E6A] not-italic">
                    — {t('about.quoteAuthor')}
                  </cite>
                </blockquote>
              </div>

              {/* Timeline */}
              <div className="mt-12">
                <h3 className="text-xl mb-8 text-[#3D3831]">{t('about.creativePath')}</h3>
                <div className="space-y-6">
                  {milestones.map((item, index) => (
                    <div key={index} className="flex items-start space-x-6">
                      <span className="text-[#B8963E] font-medium min-w-[100px]">
                        {item.year}
                      </span>
                      <div className="flex-1 pb-6 border-b border-[#E8E2D9]">
                        <p className="text-[#5A5249]">{item.event}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section ref={processRef} className="py-24 px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className={`text-center mb-16 reveal ${processVisible ? 'visible' : ''}`}>
            <span className="text-sm text-[#B8963E] tracking-widest uppercase mb-4 block">
              {t('about.process.label')}
            </span>
            <h2 className="text-4xl mb-6 text-[#3D3831]">{t('about.process.title')}</h2>
            <div className="divider-elegant" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {processSteps.map((item, index) => (
              <div
                key={item.step}
                className={`text-center p-8 reveal reveal-delay-${index + 1} ${
                  processVisible ? 'visible' : ''
                }`}
              >
                <span className="text-6xl font-light text-[#B8963E]/30 block mb-4">
                  {item.step}
                </span>
                <h3 className="text-xl mb-4 text-[#3D3831]">{item.title}</h3>
                <p className="text-[#5A5249]">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quotes Section */}
      <section ref={quotesRef} className="py-24 px-6 lg:px-8 bg-[#FAF7F2]">
        <div className="max-w-7xl mx-auto">
          <div className={`text-center mb-16 reveal ${quotesVisible ? 'visible' : ''}`}>
            <span className="text-sm text-[#B8963E] tracking-widest uppercase mb-4 block">
              {t('about.reviews.label')}
            </span>
            <h2 className="text-4xl mb-6 text-[#3D3831]">{t('about.reviews.title')}</h2>
            <div className="divider-elegant" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {reviews.map((item, index) => (
              <div
                key={index}
                className={`bg-white p-8 shadow-lg reveal reveal-delay-${index + 1} ${
                  quotesVisible ? 'visible' : ''
                }`}
              >
                <svg
                  className="w-10 h-10 text-[#B8963E]/30 mb-4"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                </svg>
                <p className="text-lg text-[#5A5249] italic mb-6 leading-relaxed">
                  "{item.quote}"
                </p>
                <div>
                  <p className="font-medium text-[#3D3831]">{item.author}</p>
                  <p className="text-sm text-[#8B7E6A]">{item.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
